/**
 * Dev/QA tooling shared state. Powers the expanded dev banner (EnvironmentBanner):
 *  - QA read-only mode: blocks write requests so a tester can "view as" another
 *    persona without mutating data.
 *  - Last API error tracking: the most recent failed request, for at-a-glance
 *    debugging without opening DevTools.
 *
 * This module is intentionally framework-free (no React) so it can be imported
 * by the axios layer (services/api.js) as well as components.
 */

const READONLY_KEY = 'qa_readonly';

// ---- QA read-only mode -------------------------------------------------------

export const isReadOnlyMode = () => {
  try {
    return localStorage.getItem(READONLY_KEY) === 'true';
  } catch {
    return false;
  }
};

export const setReadOnlyMode = (enabled) => {
  try {
    if (enabled) localStorage.setItem(READONLY_KEY, 'true');
    else localStorage.removeItem(READONLY_KEY);
  } catch {
    /* ignore storage failures */
  }
};

// Methods allowed through while read-only mode is active.
const SAFE_METHODS = ['get', 'head', 'options'];

export const isSafeMethod = (method) => SAFE_METHODS.includes(String(method || 'get').toLowerCase());

// ---- Last API error ----------------------------------------------------------

let lastApiError = null;
const errorListeners = new Set();

export const recordApiError = (error) => {
  const cfg = error?.config || {};
  lastApiError = {
    status: error?.response?.status ?? null,
    method: (cfg.method || 'get').toUpperCase(),
    url: cfg.url || '(unknown)',
    message: error?.response?.data?.detail || error?.message || 'Request failed',
    at: Date.now(),
  };
  errorListeners.forEach((fn) => {
    try {
      fn(lastApiError);
    } catch {
      /* listener errors are non-fatal */
    }
  });
};

export const getLastApiError = () => lastApiError;

export const clearLastApiError = () => {
  lastApiError = null;
  errorListeners.forEach((fn) => fn(null));
};

export const subscribeApiError = (fn) => {
  errorListeners.add(fn);
  return () => errorListeners.delete(fn);
};
